// El encargado cancela la venta de otra sucursal que retiene al cliente: permiso, motivo y qué
// decir con cada respuesta de POST /wizard/phone-holder/cancel.
import { ApiError } from '@/api/errors';
import type { CancelOtherStoreSaleResult } from '@/api/services/retail-wizard/contract';
import { formatMXN } from '@/shared/lib/format/money';
import { wizardErrorMsg } from './api/wizardError';

/** Permiso del encargado de tienda para cancelar la venta de otra sucursal. */
export const CANCEL_OTHER_STORE_PERMISSION = 'retail.wizard.cancel.other.store';

export const CANCEL_OTHER_STORE_REASON_MIN = 10;
export const CANCEL_OTHER_STORE_REASON_MAX = 200;

export function isValidCancelOtherStoreReason(reason: string): boolean {
    const length = reason.trim().length;
    return length >= CANCEL_OTHER_STORE_REASON_MIN && length <= CANCEL_OTHER_STORE_REASON_MAX;
}

/** Con enganche cobrado en la otra sucursal, queda un reembolso que hay que avisar. */
export function cancelOtherStoreSuccessMessage(result: CancelOtherStoreSaleResult): string {
    const refund = result.refundPendingCents ?? 0;
    if (refund <= 0) return 'Cancelamos la venta de la otra sucursal. Ya puedes empezar la venta.';
    return `Cancelamos la venta de la otra sucursal. Queda un reembolso pendiente de ${formatMXN(refund / 100)} del enganche; la otra sucursal lo ve en su pantalla de Reembolsos.`;
}

/**
 * `FREED`: ya no hay venta que retenga al cliente, se puede empezar. `BLOCKED`: ya no se puede
 * cancelar, se escala. `INVALID`: corregir el motivo. `RETRY`: volver a intentar.
 */
export type CancelOtherStoreOutcome = 'FREED' | 'BLOCKED' | 'INVALID' | 'RETRY';

export interface CancelOtherStoreFailure {
    outcome: CancelOtherStoreOutcome;
    message: string;
}

const FAILURE_BY_CODE: Record<string, CancelOtherStoreFailure> = {
    PHONE_HOLDER_NOT_FOUND: {
        outcome: 'FREED',
        message: 'Ya no hay una venta en curso con este teléfono. Puedes empezar la venta.',
    },
    CREDIT_NOT_PENDING: {
        outcome: 'BLOCKED',
        message:
            'La venta de la otra sucursal ya no se puede cancelar: su crédito ya avanzó. Escala el caso con el administrador.',
    },
    INVALID_SESSION_STATE: {
        outcome: 'BLOCKED',
        message:
            'La venta de la otra sucursal ya no se puede cancelar. Escala el caso con el administrador.',
    },
    VALIDATION_ERROR: {
        outcome: 'INVALID',
        message: `El motivo debe tener entre ${String(CANCEL_OTHER_STORE_REASON_MIN)} y ${String(CANCEL_OTHER_STORE_REASON_MAX)} caracteres.`,
    },
    PHONE_FILTER_INVALID: {
        outcome: 'INVALID',
        message: 'El teléfono del cliente no es válido. Revísalo y vuelve a intentar.',
    },
};

const FORBIDDEN: CancelOtherStoreFailure = {
    outcome: 'BLOCKED',
    message: 'No tienes permiso para cancelar ventas en esta tienda.',
};

export function cancelOtherStoreFailure(error: unknown): CancelOtherStoreFailure {
    const code = error instanceof ApiError ? error.code : undefined;
    const known = code !== undefined ? FAILURE_BY_CODE[code] : undefined;
    if (known) return known;
    if (error instanceof ApiError && error.httpStatus === 403) return FORBIDDEN;
    return {
        outcome: 'RETRY',
        message: wizardErrorMsg(error, 'No se pudo cancelar la venta. Intenta de nuevo.'),
    };
}
